import { createFileRoute, Link } from "@tanstack/react-router";

interface ResaleDetail {
  id: string;
  item: string;
  sku: string;
  condition: string;
  imageUrl?: string;
  seller: { name: string; email: string; payout: number };
  buyer: { name: string; email: string; paid: number };
  price: number;
  fee: number;
  status: string;
  date: string;
  timeline: Array<{ status: string; date: string; description: string }>;
}

export const Route = createFileRoute("/pin_/resale_/$id")({
  component: ResaleDetailPage,
});

// Mock data keyed by ID
const MOCK_RESALE_DATA: Record<string, ResaleDetail> = {
  "rs-mc-001": {
    id: "rs-mc-001",
    item: "Ultraboost 22",
    sku: "GX5460",
    condition: "Good",
    seller: { name: "James K.", email: "james.k@example.com", payout: 50.44 },
    buyer: { name: "Sarah M.", email: "sarah.m@example.com", paid: 52.0 },
    price: 52.0,
    fee: 1.56,
    status: "completed",
    date: "2026-02-19",
    timeline: [
      {
        status: "listed",
        date: "2026-02-14",
        description: "Item listed by James K.",
      },
      {
        status: "sold",
        date: "2026-02-17",
        description: "Purchased by Sarah M.",
      },
      {
        status: "shipped",
        date: "2026-02-18",
        description: "Shipped via USPS",
      },
      {
        status: "completed",
        date: "2026-02-19",
        description: "Delivered and confirmed",
      },
    ],
  },
  "rs-mc-002": {
    id: "rs-mc-002",
    item: "Forum Low",
    sku: "FY7756",
    condition: "Excellent",
    seller: { name: "Maria L.", email: "maria.l@example.com", payout: 65.96 },
    buyer: { name: "Tom W.", email: "tom.w@example.com", paid: 68.0 },
    price: 68.0,
    fee: 2.04,
    status: "completed",
    date: "2026-02-19",
    timeline: [
      {
        status: "listed",
        date: "2026-02-12",
        description: "Item listed by Maria L.",
      },
      {
        status: "sold",
        date: "2026-02-16",
        description: "Purchased by Tom W.",
      },
      {
        status: "shipped",
        date: "2026-02-17",
        description: "Shipped via FedEx",
      },
      {
        status: "completed",
        date: "2026-02-19",
        description: "Delivered and confirmed",
      },
    ],
  },
  "rs-mc-003": {
    id: "rs-mc-003",
    item: "Samba OG",
    sku: "B75806",
    condition: "Good",
    seller: { name: "Chen R.", email: "chen.r@example.com", payout: 43.65 },
    buyer: { name: "Alex P.", email: "alex.p@example.com", paid: 45.0 },
    price: 45.0,
    fee: 1.35,
    status: "shipped",
    date: "2026-02-18",
    timeline: [
      {
        status: "listed",
        date: "2026-02-13",
        description: "Item listed by Chen R.",
      },
      {
        status: "sold",
        date: "2026-02-17",
        description: "Purchased by Alex P.",
      },
      { status: "shipped", date: "2026-02-18", description: "Shipped via UPS" },
    ],
  },
  "rs-mc-004": {
    id: "rs-mc-004",
    item: "Stan Smith",
    sku: "FX5502",
    condition: "Fair",
    seller: { name: "Emily D.", email: "emily.d@example.com", payout: 36.86 },
    buyer: { name: "Jordan B.", email: "jordan.b@example.com", paid: 38.0 },
    price: 38.0,
    fee: 1.14,
    status: "pending",
    date: "2026-02-18",
    timeline: [
      {
        status: "listed",
        date: "2026-02-15",
        description: "Item listed by Emily D.",
      },
      {
        status: "sold",
        date: "2026-02-18",
        description: "Purchased by Jordan B.",
      },
    ],
  },
  "rs-mc-005": {
    id: "rs-mc-005",
    item: "Gazelle",
    sku: "BB5476",
    condition: "Good",
    seller: { name: "David H.", email: "david.h@example.com", payout: 53.35 },
    buyer: { name: "Lisa N.", email: "lisa.n@example.com", paid: 55.0 },
    price: 55.0,
    fee: 1.65,
    status: "completed",
    date: "2026-02-18",
    timeline: [
      {
        status: "listed",
        date: "2026-02-10",
        description: "Item listed by David H.",
      },
      {
        status: "sold",
        date: "2026-02-14",
        description: "Purchased by Lisa N.",
      },
      {
        status: "shipped",
        date: "2026-02-15",
        description: "Shipped via USPS",
      },
      {
        status: "completed",
        date: "2026-02-18",
        description: "Delivered and confirmed",
      },
    ],
  },
};

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    completed: "#00FBA9",
    shipped: "#4A90D9",
    sold: "#7B61FF",
    listed: "#F5A623",
    pending: "#A9A9A9",
  };
  return colors[status.toLowerCase()] || "#A9A9A9";
}

function DemoBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full"
      style={{ backgroundColor: "rgba(0,251,169,0.15)", color: "#171717" }}
    >
      Demo Data
    </span>
  );
}

function ResaleDetailPage() {
  const { id } = Route.useParams();

  // Look up mock data, or generate a fallback
  const resale = MOCK_RESALE_DATA[id] || {
    id,
    item: "Unknown Item",
    sku: "N/A",
    condition: "N/A",
    seller: { name: "Unknown", email: "unknown@example.com", payout: 0 },
    buyer: { name: "Unknown", email: "unknown@example.com", paid: 0 },
    price: 0,
    fee: 0,
    status: "unknown",
    date: "N/A",
    timeline: [],
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div
        className="flex items-center gap-2 text-sm mb-6"
        style={{ color: "#A9A9A9" }}
      >
        <Link to="/programs" className="hover:underline">
          Programs
        </Link>
        <span>/</span>
        <span className="hover:underline">Resale</span>
        <span>/</span>
        <span style={{ color: "#171717" }}>{resale.id}</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div
            className="w-14 h-14 flex items-center justify-center"
            style={{ backgroundColor: "#00FBA9" }}
          >
            <ResaleIcon />
          </div>
          <div>
            <h1 className="text-2xl font-light" style={{ color: "#171717" }}>
              {resale.item}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <span
                className="px-2 py-0.5 rounded-full text-xs capitalize"
                style={{
                  backgroundColor: `${getStatusColor(resale.status)}20`,
                  color: getStatusColor(resale.status),
                }}
              >
                {resale.status}
              </span>
              <span className="text-sm font-mono" style={{ color: "#A9A9A9" }}>
                {resale.id}
              </span>
              <DemoBadge />
            </div>
          </div>
        </div>
        <div className="text-right">
          <p className="text-3xl font-light" style={{ color: "#171717" }}>
            ${resale.price.toFixed(2)}
          </p>
          <p className="text-sm" style={{ color: "#A9A9A9" }}>
            Sale Price
          </p>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Seller Card */}
        <div className="card">
          <h3
            className="text-xs font-medium mb-4 uppercase tracking-wide"
            style={{ color: "#A9A9A9" }}
          >
            Seller
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium"
                style={{ backgroundColor: "#FAFAFA", color: "#171717" }}
              >
                {resale.seller.name.charAt(0)}
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: "#171717" }}>
                  {resale.seller.name}
                </p>
                <p className="text-xs" style={{ color: "#A9A9A9" }}>
                  {resale.seller.email}
                </p>
              </div>
            </div>
            <div
              className="pt-3"
              style={{ borderTop: "1px solid rgba(169,169,169,0.1)" }}
            >
              <p className="text-xs" style={{ color: "#A9A9A9" }}>
                Payout Amount
              </p>
              <p className="text-xl font-light" style={{ color: "#171717" }}>
                ${resale.seller.payout.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Buyer Card */}
        <div className="card">
          <h3
            className="text-xs font-medium mb-4 uppercase tracking-wide"
            style={{ color: "#A9A9A9" }}
          >
            Buyer
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium"
                style={{ backgroundColor: "#FAFAFA", color: "#171717" }}
              >
                {resale.buyer.name.charAt(0)}
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: "#171717" }}>
                  {resale.buyer.name}
                </p>
                <p className="text-xs" style={{ color: "#A9A9A9" }}>
                  {resale.buyer.email}
                </p>
              </div>
            </div>
            <div
              className="pt-3"
              style={{ borderTop: "1px solid rgba(169,169,169,0.1)" }}
            >
              <p className="text-xs" style={{ color: "#A9A9A9" }}>
                Amount Paid
              </p>
              <p className="text-xl font-light" style={{ color: "#171717" }}>
                ${resale.buyer.paid.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Product Card */}
        <div className="card">
          <h3
            className="text-xs font-medium mb-4 uppercase tracking-wide"
            style={{ color: "#A9A9A9" }}
          >
            Product Details
          </h3>
          <div className="space-y-2 text-sm">
            <p>
              <span style={{ color: "#A9A9A9" }}>Item:</span>{" "}
              <span style={{ color: "#171717" }}>{resale.item}</span>
            </p>
            <p>
              <span style={{ color: "#A9A9A9" }}>SKU:</span>{" "}
              <span className="font-mono text-xs" style={{ color: "#171717" }}>
                {resale.sku}
              </span>
            </p>
            <p>
              <span style={{ color: "#A9A9A9" }}>Condition:</span>{" "}
              <span style={{ color: "#171717" }}>{resale.condition}</span>
            </p>
            <p>
              <span style={{ color: "#A9A9A9" }}>Date:</span>{" "}
              <span style={{ color: "#171717" }}>{resale.date}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Payment Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="card">
          <h3
            className="text-xs font-medium mb-4 uppercase tracking-wide"
            style={{ color: "#A9A9A9" }}
          >
            Payment Breakdown
          </h3>
          <div className="space-y-3">
            <div
              className="flex justify-between items-center py-2"
              style={{ borderBottom: "1px solid rgba(169,169,169,0.1)" }}
            >
              <span className="text-sm" style={{ color: "#A9A9A9" }}>
                Item Price
              </span>
              <span
                className="text-sm font-medium"
                style={{ color: "#171717" }}
              >
                ${resale.price.toFixed(2)}
              </span>
            </div>
            <div
              className="flex justify-between items-center py-2"
              style={{ borderBottom: "1px solid rgba(169,169,169,0.1)" }}
            >
              <span className="text-sm" style={{ color: "#A9A9A9" }}>
                Platform Fee (3%)
              </span>
              <span
                className="text-sm font-medium"
                style={{ color: "#00FBA9" }}
              >
                ${(resale.price * 0.03).toFixed(2)}
              </span>
            </div>
            <div
              className="flex justify-between items-center py-2"
              style={{ borderBottom: "1px solid rgba(169,169,169,0.1)" }}
            >
              <span className="text-sm" style={{ color: "#A9A9A9" }}>
                Seller Payout
              </span>
              <span
                className="text-sm font-medium"
                style={{ color: "#171717" }}
              >
                ${(resale.price * 0.97).toFixed(2)}
              </span>
            </div>
            <div
              className="flex justify-between items-center py-2"
              style={{ borderBottom: "1px solid rgba(169,169,169,0.1)" }}
            >
              <span className="text-sm" style={{ color: "#A9A9A9" }}>
                MasterCard Processing
              </span>
              <span
                className="text-sm font-medium"
                style={{ color: "#171717" }}
              >
                ${(resale.price * 0.029).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span
                className="text-sm font-medium"
                style={{ color: "#171717" }}
              >
                Net Platform Revenue
              </span>
              <span
                className="text-sm font-medium"
                style={{ color: "#00FBA9" }}
              >
                ${(resale.price * 0.03 - resale.price * 0.029).toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Transaction Timeline */}
        <div className="card">
          <h3
            className="text-xs font-medium mb-4 uppercase tracking-wide"
            style={{ color: "#A9A9A9" }}
          >
            Transaction Timeline
          </h3>
          <div className="space-y-4">
            {resale.timeline.map((event, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: getStatusColor(event.status) }}
                  />
                  {i < resale.timeline.length - 1 && (
                    <div
                      className="w-px h-8"
                      style={{ backgroundColor: "rgba(169,169,169,0.2)" }}
                    />
                  )}
                </div>
                <div className="flex-1 -mt-0.5">
                  <p
                    className="text-sm capitalize"
                    style={{ color: "#171717" }}
                  >
                    {event.status}
                  </p>
                  <p className="text-xs" style={{ color: "#A9A9A9" }}>
                    {event.description}
                  </p>
                  <p className="text-xs" style={{ color: "#A9A9A9" }}>
                    {event.date}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Icons
function ResaleIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#171717"
      strokeWidth="1.5"
    >
      <path
        d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M9 22V12H15V22" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
